import './instrumentation';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/carousel/styles.css';

import { Suspense } from 'react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import {
  Button,
  ColorSchemeScript,
  LoadingOverlay,
  localStorageColorSchemeManager,
  MantineProvider,
  Stack,
  Text,
} from '@mantine/core';
import { notifications, Notifications } from '@mantine/notifications';
import { cssVariablesResolver, theme } from './theme';

import './i18n';

import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { App } from './App';
import { isRetryableApiError } from './stores/api/apiClient';
import { purgeLegacyOfflineDb } from './utils/purgeLegacyOfflineDb';

import 'dayjs/locale/en';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/pt-br';

import data from '@emoji-mart/data';
import { QueryNormalizerProvider } from '@normy/react-query';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { init } from 'emoji-mart';
import { Helmet } from 'react-helmet';
import { registerSW } from 'virtual:pwa-register';

init({ data });

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

const colorSchemeManager = localStorageColorSchemeManager({
  key: 'ametista-ai-color-scheme',
});

const updateSW = registerSW({
  onNeedRefresh() {
    console.log('Need refresh');
    notifications.show({
      id: 'pwa-update',
      color: 'blue',
      title: 'Atualização disponível',
      message: (
        <Stack gap="xs">
          <Text>Atualize a aplicação para a versão mais recente.</Text>
          <Button onClick={() => updateSW(true)}>Atualizar</Button>
        </Stack>
      ),
      autoClose: false,
    });
  },
  onOfflineReady() {
    console.log('Offline ready');
  },
  onRegisteredSW(swUrl, r) {
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    r &&
      setInterval(
        async () => {
          console.log('Checking for updates');

          try {
            if (r.installing || !navigator) {
              return;
            }

            if ('connection' in navigator && !navigator.onLine) {
              return;
            }

            const resp = await fetch(swUrl, {
              cache: 'no-store',
              headers: {
                cache: 'no-store',
                'cache-control': 'no-cache',
              },
            });

            if (resp?.status === 200) {
              await r.update();
            }
          } catch (error) {
            console.error('Error checking for updates', error);
          }
        },
        60 * 60 * 1000 // 1 hour
      );
  },
});

window.addEventListener('error', (event) => {
  if (
    event.message?.includes('Failed to fetch dynamically imported module') ||
    (event?.target instanceof HTMLScriptElement && event.target.tagName === 'SCRIPT')
  ) {
    console.warn('Chunk load failed, reloading page...');
    window.location.reload();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => isRetryableApiError(error) && failureCount < 3,
      staleTime: 5 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    },
  },
});

void purgeLegacyOfflineDb();

root.render(
  <QueryNormalizerProvider queryClient={queryClient}>
    <QueryClientProvider client={queryClient}>
      <ColorSchemeScript defaultColorScheme="light" />
      <MantineProvider
        theme={theme}
        colorSchemeManager={colorSchemeManager}
        cssVariablesResolver={cssVariablesResolver}
        defaultColorScheme="light"
      >
        <Helmet defaultTitle="Ametista AI" titleTemplate="%s | Ametista AI" />
        <Notifications />
        <Suspense fallback={<LoadingOverlay />}>
          <BrowserRouter>
            <NuqsAdapter>
              <App />
            </NuqsAdapter>
          </BrowserRouter>
        </Suspense>
      </MantineProvider>
    </QueryClientProvider>
  </QueryNormalizerProvider>
);
